import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabase';

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function fmtHora(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const ESTADOS = [
  { id: 'abierta', label: 'Abierta', color: '#3B82F6' },
  { id: 'perdida', label: 'Perdida', color: '#A32D2D' },
  { id: 'cerrada', label: 'Cerrada', color: '#666' },
];

export default function Inbox({ miRol, miAgente }) {
  const isMobile = useIsMobile();
  const [desarrollos, setDesarrollos] = useState([]);
  const [agentesTodos, setAgentesTodos] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [conversaciones, setConversaciones] = useState([]);
  const [mensajes, setMensajes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buscar, setBuscar] = useState('');
  const [filtroAsesor, setFiltroAsesor] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroEstrellas, setFiltroEstrellas] = useState('');
  const [filtroModo, setFiltroModo] = useState('');
  const [soloVentana24h, setSoloVentana24h] = useState(false);
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [showFiltros, setShowFiltros] = useState(false);
  const [conversacionSel, setConversacionSel] = useState(null);
  const [textoReply, setTextoReply] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState('');
  const [plantillaSel, setPlantillaSel] = useState('');
  const [variablesPlantilla, setVariablesPlantilla] = useState({});
  const scrollRef = useRef(null);

  const esAdminGeneral = miRol === 'Super Admin' || miRol === 'Admin' || miRol === 'Sub Admin';
  const esGerente = miRol === 'Gerente Editor' || miRol === 'Gerente Operador' || miRol === 'Gerente Externo';

  useEffect(() => { cargarDesarrollos(); cargarAgentesTodos(); cargarPlantillas(); }, []);
  useEffect(() => { cargarConversaciones(); }, [desarrollos]);
  useEffect(() => { if (conversacionSel) cargarMensajes(conversacionSel.id); }, [conversacionSel?.id]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [mensajes]);

  // FIX: WhatsApp en vivo -- se suscribe a inserts/updates de mensajes y
  // conversaciones (Realtime) y simplemente vuelve a llamar a los mismos
  // cargarX() ya existentes, sin duplicar la lógica de fetch.
  useEffect(() => {
    const canal = supabase
      .channel('inbox-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'mensajes' }, (payload) => {
        const convId = payload.new?.conversacion_id || payload.old?.conversacion_id;
        if (conversacionSel && convId === conversacionSel.id) cargarMensajes(conversacionSel.id);
        cargarConversaciones();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'conversaciones' }, () => {
        cargarConversaciones();
      })
      .subscribe();
    return () => { supabase.removeChannel(canal); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversacionSel?.id, desarrollos, miRol, miAgente]);

  const cargarDesarrollos = async () => {
    let query = supabase.from('desarrollos').select('id, nombre').order('nombre');
    if (!esAdminGeneral) {
      const asignados = miAgente?.desarrollos_cargo || [];
      if (asignados.length === 0) { setDesarrollos([]); return; }
      query = query.in('nombre', asignados);
    }
    const { data } = await query;
    setDesarrollos(data || []);
  };

  const cargarAgentesTodos = async () => {
    const { data } = await supabase.from('agentes').select('correo, nombre, apellidos').eq('activo', true).order('nombre');
    setAgentesTodos(data || []);
  };

  const cargarPlantillas = async () => {
    const { data } = await supabase.from('wa_templates').select('*').eq('estado', 'aprobada').order('nombre');
    setPlantillas(data || []);
  };

  // FIX: mismo criterio de alcance por rol que ya usan Negocios.js/
  // Contactos.js -- Agente ve solo lo suyo (asesor_asignado), Gerente ve
  // sus desarrollos a cargo, Admin/Super Admin/Sub Admin ven todo.
  const cargarConversaciones = useCallback(async () => {
    setLoading(true);
    let query = supabase.from('conversaciones').select('*, desarrollos(nombre)').order('ultimo_mensaje_at', { ascending: false });
    if (esAdminGeneral) {
      // sin restricción
    } else if (esGerente) {
      const cargo = miAgente?.desarrollos_cargo || [];
      if (cargo.length === 0) { setConversaciones([]); setLoading(false); return; }
      const ids = desarrollos.filter(d => cargo.includes(d.nombre)).map(d => d.id);
      if (ids.length === 0) { setConversaciones([]); setLoading(false); return; }
      query = query.in('desarrollo_id', ids);
    } else {
      query = query.eq('asesor_asignado', miAgente?.correo || '__ninguno__');
    }
    const { data } = await query;
    setConversaciones(data || []);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esAdminGeneral, esGerente, miAgente, desarrollos]);

  useEffect(() => { cargarConversaciones(); }, [cargarConversaciones]);

  const cargarMensajes = async (conversacionId) => {
    const { data } = await supabase.from('mensajes').select('*').eq('conversacion_id', conversacionId).order('created_at', { ascending: true });
    setMensajes(data || []);
  };

  const ventanaActiva = (c) => !!c?.ventana_24h_expira_en && new Date(c.ventana_24h_expira_en) > new Date();

  const conversacionesFiltradas = conversaciones.filter(c => {
    if (buscar && !(c.nombre_contacto?.toLowerCase().includes(buscar.toLowerCase()) || c.telefono_contacto?.includes(buscar))) return false;
    if (filtroAsesor && c.asesor_asignado !== filtroAsesor) return false;
    if (filtroEstado && (c.estado || 'abierta') !== filtroEstado) return false;
    if (filtroEstrellas && (c.estrellas || 0) < Number(filtroEstrellas)) return false;
    if (filtroModo && c.modo !== filtroModo) return false;
    if (soloVentana24h && !ventanaActiva(c)) return false;
    if (soloPendientes && !c.pendiente_respuesta) return false;
    return true;
  });

  const handleEnviar = async () => {
    if (!textoReply.trim() || !conversacionSel || enviando) return;
    setEnviando(true); setErrorEnvio('');
    const { data: sesion } = await supabase.auth.getSession();
    const { data, error } = await supabase.functions.invoke('wa-enviar-mensaje', {
      body: { conversacion_id: conversacionSel.id, texto: textoReply.trim() },
      headers: { Authorization: `Bearer ${sesion?.session?.access_token}` },
    });
    if (error || data?.error) {
      setErrorEnvio(data?.error || error?.message || 'Error al enviar el mensaje');
    } else {
      setTextoReply('');
      cargarMensajes(conversacionSel.id);
      cargarConversaciones();
    }
    setEnviando(false);
  };

  const handleEnviarPlantilla = async () => {
    if (!plantillaSel || !conversacionSel || enviando) return;
    setEnviando(true); setErrorEnvio('');
    const plantilla = plantillas.find(p => p.id === plantillaSel);
    const variables = (plantilla?.variables || []).map(v => variablesPlantilla[v] || '');
    const { data: sesion } = await supabase.auth.getSession();
    const { data, error } = await supabase.functions.invoke('wa-enviar-mensaje', {
      body: { conversacion_id: conversacionSel.id, plantilla_id: plantillaSel, variables },
      headers: { Authorization: `Bearer ${sesion?.session?.access_token}` },
    });
    if (error || data?.error) {
      setErrorEnvio(data?.error || error?.message || 'Error al enviar la plantilla');
    } else {
      setPlantillaSel(''); setVariablesPlantilla({});
      cargarMensajes(conversacionSel.id);
      cargarConversaciones();
    }
    setEnviando(false);
  };

  const actualizarConversacion = async (campos) => {
    if (!conversacionSel) return;
    await supabase.from('conversaciones').update(campos).eq('id', conversacionSel.id);
    setConversacionSel(prev => ({ ...prev, ...campos }));
    cargarConversaciones();
  };

  const handleCambiarModo = (nuevoModo) => actualizarConversacion({ modo: nuevoModo });
  const handleCambiarEstrellas = (n) => actualizarConversacion({ estrellas: n === conversacionSel.estrellas ? 0 : n });
  const handleCambiarEstado = (nuevoEstado) => actualizarConversacion({ estado: nuevoEstado });
  const handleCambiarAsesor = (correo) => actualizarConversacion({ asesor_asignado: correo || null });
  const handleCambiarDesarrollo = (id) => actualizarConversacion({ desarrollo_id: id || null });
  const [notasDraft, setNotasDraft] = useState('');
  useEffect(() => { setNotasDraft(conversacionSel?.notas_asesor || ''); }, [conversacionSel?.id]);
  const handleGuardarNotas = () => actualizarConversacion({ notas_asesor: notasDraft });

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem', height: isMobile ? 'auto' : 'calc(100vh - 0px)', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexShrink: 0, flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h2 style={{ fontSize: isMobile ? '17px' : '20px', fontWeight: '500', color: '#1a1a2e', marginBottom: '4px' }}>Chats</h2>
          <div style={{ fontSize: '13px', color: '#888' }}>{conversaciones.length} conversaciones</div>
        </div>
        <button onClick={() => setShowFiltros(v => !v)}
          style={{ padding: '8px 16px', background: '#fff', color: '#333', border: '0.5px solid #ddd', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
          ⚙️ Filtros
        </button>
      </div>

      {showFiltros && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '1rem', flexShrink: 0 }}>
          {(esAdminGeneral || esGerente) && (
            <select value={filtroAsesor} onChange={e => setFiltroAsesor(e.target.value)} style={selStyle}>
              <option value=''>Todos los asesores</option>
              {agentesTodos.map(a => <option key={a.correo} value={a.correo}>{a.nombre} {a.apellidos}</option>)}
            </select>
          )}
          <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={selStyle}>
            <option value=''>Todos los estados</option>
            {ESTADOS.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
          </select>
          <select value={filtroEstrellas} onChange={e => setFiltroEstrellas(e.target.value)} style={selStyle}>
            <option value=''>Cualquier calificación</option>
            {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}★ o más</option>)}
          </select>
          <select value={filtroModo} onChange={e => setFiltroModo(e.target.value)} style={selStyle}>
            <option value=''>Bot y humano</option>
            <option value='bot'>Solo bot</option>
            <option value='humano'>Solo humano</option>
          </select>
          <label style={chipStyle}>
            <input type='checkbox' checked={soloVentana24h} onChange={e => setSoloVentana24h(e.target.checked)} /> Ventana 24h activa
          </label>
          <label style={chipStyle}>
            <input type='checkbox' checked={soloPendientes} onChange={e => setSoloPendientes(e.target.checked)} /> Pendientes de responder
          </label>
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', gap: '16px', minHeight: 0, flexDirection: isMobile ? 'column' : 'row' }}>
        {/* Lista de conversaciones */}
        <div style={{ width: isMobile ? '100%' : '340px', flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden', maxHeight: isMobile && conversacionSel ? '200px' : 'none' }}>
          <div style={{ padding: '10px', borderBottom: '0.5px solid #f0f0f0', display: 'flex', gap: '8px', flexShrink: 0 }}>
            <input placeholder='Buscar...' value={buscar} onChange={e => setBuscar(e.target.value)}
              style={{ flex: 1, padding: '8px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px' }} />
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#888', fontSize: '13px' }}>Cargando...</div>
            ) : conversacionesFiltradas.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#aaa', fontSize: '13px' }}>Sin conversaciones</div>
            ) : conversacionesFiltradas.map(c => (
              <div key={c.id} onClick={() => setConversacionSel(c)}
                style={{ padding: '12px 14px', borderBottom: '0.5px solid #f5f5f5', cursor: 'pointer', background: conversacionSel?.id === c.id ? '#f5f5f5' : '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e' }}>{c.nombre_contacto || c.telefono_contacto}</span>
                  <span style={{ fontSize: '10px', color: '#aaa' }}>{fmtHora(c.ultimo_mensaje_at)}</span>
                </div>
                <div style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.ultimo_mensaje_preview || 'Sin mensajes'}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', gap: '6px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '10px', color: '#bbb' }}>{c.desarrollos?.nombre || 'Sin desarrollo'}</span>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    {c.pendiente_respuesta && <span title='Pendiente de responder' style={{ fontSize: '9px' }}>🔴</span>}
                    {c.estrellas > 0 && <span style={{ fontSize: '9px', color: '#D4A017' }}>{'★'.repeat(c.estrellas)}</span>}
                    <span style={{ fontSize: '9px', padding: '1px 7px', borderRadius: '10px', fontWeight: '600', background: c.modo === 'humano' ? '#EAF3DE' : '#EEF2FF', color: c.modo === 'humano' ? '#27500A' : '#4338CA' }}>
                      {c.modo === 'humano' ? '👤' : '🤖'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Hilo de mensajes */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden', minHeight: isMobile ? '400px' : 0 }}>
          {!conversacionSel ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: '13px' }}>
              Selecciona una conversación
            </div>
          ) : (
            <>
              <div style={{ padding: '14px 18px', borderBottom: '0.5px solid #f0f0f0', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a2e' }}>{conversacionSel.nombre_contacto || conversacionSel.telefono_contacto}</div>
                  <div style={{ fontSize: '12px', color: '#888' }}>{conversacionSel.telefono_contacto} · {conversacionSel.desarrollos?.nombre || 'Sin desarrollo'}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '20px', fontWeight: '600', background: ventanaActiva(conversacionSel) ? '#EAF3DE' : '#FCEBEB', color: ventanaActiva(conversacionSel) ? '#27500A' : '#A32D2D' }}>
                    {ventanaActiva(conversacionSel) ? '🟢 Ventana 24h activa' : '🔴 Ventana 24h vencida'}
                  </span>
                  <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '20px', fontWeight: '600', background: conversacionSel.modo === 'humano' ? '#EAF3DE' : '#EEF2FF', color: conversacionSel.modo === 'humano' ? '#27500A' : '#4338CA' }}>
                    {conversacionSel.modo === 'humano' ? '👤 Modo Humano' : '🤖 Modo Bot'}
                  </span>
                  {conversacionSel.modo === 'bot' ? (
                    <button onClick={() => handleCambiarModo('humano')} style={btnSecundario}>Tomar conversación</button>
                  ) : (
                    <button onClick={() => handleCambiarModo('bot')} style={btnOutline}>Devolver al bot</button>
                  )}
                </div>
              </div>
              <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {mensajes.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#aaa', fontSize: '13px', marginTop: '2rem' }}>Sin mensajes aún</div>
                ) : mensajes.map(m => (
                  <div key={m.id} style={{ alignSelf: m.direccion === 'saliente' ? 'flex-end' : 'flex-start', maxWidth: '70%' }}>
                    <div style={{ padding: '10px 14px', borderRadius: '14px', fontSize: '13px', background: m.direccion === 'saliente' ? '#4A4A4A' : '#f0f0f0', color: m.direccion === 'saliente' ? '#fff' : '#333' }}>
                      {m.tipo === 'plantilla' && <div style={{ fontSize: '10px', opacity: 0.7, marginBottom: '3px' }}>📋 Plantilla: {m.plantilla_nombre}</div>}
                      {m.media_url && (m.tipo === 'imagen'
                        ? <img src={m.media_url} alt='' style={{ maxWidth: '220px', borderRadius: '8px', display: 'block', marginBottom: m.texto ? '6px' : 0 }} />
                        : <a href={m.media_url} target='_blank' rel='noreferrer' style={{ color: 'inherit', textDecoration: 'underline', display: 'block', marginBottom: m.texto ? '6px' : 0 }}>📎 Ver {m.tipo}</a>)}
                      {m.texto}
                    </div>
                    <div style={{ fontSize: '10px', color: '#bbb', marginTop: '3px', textAlign: m.direccion === 'saliente' ? 'right' : 'left' }}>
                      {fmtHora(m.created_at)}{m.direccion === 'saliente' && m.estado_envio ? ` · ${m.estado_envio}` : ''}
                    </div>
                  </div>
                ))}
              </div>
              {errorEnvio && (
                <div style={{ margin: '0 14px 8px', padding: '8px 10px', background: '#FCEBEB', color: '#A32D2D', borderRadius: '6px', fontSize: '12px' }}>{errorEnvio}</div>
              )}
              {ventanaActiva(conversacionSel) ? (
                <div style={{ padding: '12px 14px', borderTop: '0.5px solid #f0f0f0', display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <input value={textoReply} onChange={e => setTextoReply(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleEnviar(); }}
                    placeholder='Escribe un mensaje...'
                    style={{ flex: 1, padding: '10px 12px', border: '0.5px solid #ddd', borderRadius: '8px', fontSize: '14px' }} />
                  <button onClick={handleEnviar} disabled={enviando || !textoReply.trim()} style={btnSecundario}>Enviar</button>
                </div>
              ) : (
                <div style={{ padding: '12px 14px', borderTop: '0.5px solid #f0f0f0', flexShrink: 0 }}>
                  <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>Ventana de 24h vencida — solo se puede enviar una plantilla aprobada</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <select value={plantillaSel} onChange={e => { setPlantillaSel(e.target.value); setVariablesPlantilla({}); }} style={{ ...selStyle, flex: 1, minWidth: '160px' }}>
                      <option value=''>Elige una plantilla...</option>
                      {plantillas.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                    </select>
                    <button onClick={handleEnviarPlantilla} disabled={enviando || !plantillaSel} style={btnSecundario}>Enviar plantilla</button>
                  </div>
                  {plantillaSel && (plantillas.find(p => p.id === plantillaSel)?.variables || []).map(v => (
                    <input key={v} placeholder={v} value={variablesPlantilla[v] || ''} onChange={e => setVariablesPlantilla(prev => ({ ...prev, [v]: e.target.value }))}
                      style={{ marginTop: '6px', width: '100%', padding: '8px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                  ))}
                  {plantillas.length === 0 && <div style={{ fontSize: '11px', color: '#aaa', marginTop: '6px' }}>Todavía no hay plantillas aprobadas cargadas.</div>}
                </div>
              )}
            </>
          )}
        </div>

        {/* Panel lateral de detalle */}
        {conversacionSel && !isMobile && (
          <div style={{ width: '300px', flexShrink: 0, background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '18px', overflowY: 'auto' }}>
            <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>ASESOR ASIGNADO</div>
            <select value={conversacionSel.asesor_asignado || ''} onChange={e => handleCambiarAsesor(e.target.value)} style={{ ...selStyle, width: '100%', marginBottom: '16px' }}>
              <option value=''>Sin asignar</option>
              {agentesTodos.map(a => <option key={a.correo} value={a.correo}>{a.nombre} {a.apellidos}</option>)}
            </select>

            <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>DESARROLLO</div>
            <select value={conversacionSel.desarrollo_id || ''} onChange={e => handleCambiarDesarrollo(e.target.value)} style={{ ...selStyle, width: '100%', marginBottom: '16px' }}>
              <option value=''>Sin asignar</option>
              {desarrollos.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
            </select>

            <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>CALIFICACIÓN</div>
            <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
              {[1, 2, 3, 4, 5].map(n => (
                <span key={n} onClick={() => handleCambiarEstrellas(n)} style={{ fontSize: '20px', cursor: 'pointer', color: n <= (conversacionSel.estrellas || 0) ? '#D4A017' : '#e0e0e0' }}>★</span>
              ))}
            </div>

            <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>ESTADO</div>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {ESTADOS.map(e => (
                <button key={e.id} onClick={() => handleCambiarEstado(e.id)}
                  style={{ padding: '5px 12px', borderRadius: '16px', border: `1px solid ${e.color}`, background: (conversacionSel.estado || 'abierta') === e.id ? e.color : 'transparent', color: (conversacionSel.estado || 'abierta') === e.id ? '#fff' : e.color, fontSize: '11px', cursor: 'pointer' }}>
                  {e.label}
                </button>
              ))}
            </div>

            <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>NOTAS DEL ASESOR</div>
            <textarea value={notasDraft} onChange={e => setNotasDraft(e.target.value)} rows={4}
              style={{ width: '100%', padding: '8px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box', resize: 'vertical', marginBottom: '8px' }} />
            <button onClick={handleGuardarNotas} style={{ ...btnOutline, width: '100%' }}>Guardar notas</button>
          </div>
        )}
      </div>
    </div>
  );
}

const selStyle = { padding: '8px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', background: '#fff' };
const chipStyle = { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#555', padding: '8px 10px', border: '0.5px solid #ddd', borderRadius: '6px', background: '#fff', cursor: 'pointer' };
const btnSecundario = { padding: '10px 18px', background: '#4A4A4A', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' };
const btnOutline = { padding: '8px 14px', background: '#fff', color: '#333', border: '0.5px solid #ddd', borderRadius: '8px', fontSize: '12px', cursor: 'pointer' };
